import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { settingsApi, authApi } from '@/services/api';
import { useNavigate } from 'react-router-dom';

export function useSessionTimeout() {
  const { isAuthenticated, clearAuth } = useAuthStore();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(120); // 2 minutes
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const sessionTimeoutMinutes = useRef(30);

  // Fetch settings for timeout
  useEffect(() => {
    if (isAuthenticated) {
      settingsApi.getAll().then((res) => {
        const minutes = parseInt(res.data.data.session_timeout_minutes || '30', 10);
        sessionTimeoutMinutes.current = minutes;
        resetTimeout();
      }).catch(console.error);
    }
  }, [isAuthenticated]);

  const handleLogout = () => {
    authApi.logout().catch(console.error);
    clearAuth();
    navigate('/login');
  };

  const resetTimeout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    setShowWarning(false);
    setCountdown(120);

    const ms = sessionTimeoutMinutes.current * 60 * 1000;
    
    warningRef.current = setTimeout(() => {
      setShowWarning(true);
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, ms - 120 * 1000); // 2 mins before

    timeoutRef.current = setTimeout(handleLogout, ms);
  };

  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousemove', 'keydown', 'click', 'touchstart'];
    const handleActivity = () => resetTimeout();

    events.forEach((event) => window.addEventListener(event, handleActivity));
    resetTimeout();

    return () => {
      events.forEach((event) => window.removeEventListener(event, handleActivity));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [isAuthenticated]);

  return { showWarning, countdown, stayLoggedIn: resetTimeout };
}
